import styled from 'styled-components';
import { theme } from '@/styles/theme';
import { Container, Section } from '@/components/ui/Container';
import { FadeInWhenVisible } from '@/components/animations/FadeInWhenVisible';
import { History, Target, Heart, Users2 } from 'lucide-react';
import bjornPhoto from '@/assets/images/n-leegn-pupiter_c-13-_800 (1).webp';
import boardPhoto from '@/assets/images/406014186_10160763683957850_428031718400352232_n-1024x684-1 (1).webp';
import { AnimatedCounter } from '@/components/ui/AnimatedCounter';
import { useState, useEffect } from 'react';
import { client } from '@/api/client';

const PageHeader = styled(Section)`
  background: ${theme.colors.primary.navy};
  color: ${theme.colors.neutral.white};
  text-align: center;
  padding: ${theme.spacing[24]} 0 ${theme.spacing[16]};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[16]} 0 ${theme.spacing[12]};
  }
`;

const PageTitle = styled.h1`
  color: ${theme.colors.neutral.white};
  margin-bottom: ${theme.spacing[4]};
  font-size: ${theme.fontSizes['5xl']};
  font-weight: ${theme.fontWeights.bold};
  letter-spacing: '-0.02em';
  line-height: ${theme.lineHeights.tight};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes['4xl']};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.primary.lightGold};
  max-width: 700px;
  margin: 0 auto;
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[12]};
  align-items: center;
  margin-bottom: ${theme.spacing[16]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[8]};
  }
`;

const TextContent = styled.div`
  h2 {
    color: ${theme.colors.primary.navy};
    margin-bottom: ${theme.spacing[4]};
  }

  h3 {
    color: ${theme.colors.primary.navy};
    margin-bottom: ${theme.spacing[3]};
    margin-top: ${theme.spacing[6]};
  }

  p {
    color: ${theme.colors.neutral.gray};
    line-height: ${theme.lineHeights.relaxed};
    margin-bottom: ${theme.spacing[4]};

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    color: ${theme.colors.neutral.gray};
    line-height: ${theme.lineHeights.relaxed};
    margin-bottom: ${theme.spacing[4]};
    padding-left: ${theme.spacing[6]};
  }

  li {
    margin-bottom: ${theme.spacing[2]};
  }
`;

const IconBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const IconCircle = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  background: ${theme.colors.primary.navy};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary.gold};
`;

const IconContent = styled.div`
  h3 {
    color: ${theme.colors.primary.navy};
    margin-bottom: ${theme.spacing[2]};
    font-size: ${theme.fontSizes.xl};
  }

  p {
    color: ${theme.colors.neutral.gray};
    line-height: ${theme.lineHeights.relaxed};
  }
`;

const DirectorImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: 500px;
  object-fit: cover;
  border-radius: ${theme.borderRadius['2xl']};
  margin: 0 auto;
  display: block;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const BoardImage = styled.img`
  width: 100%;
  height: 600px;
  object-fit: cover;
  object-position: center 20%;
  border-radius: ${theme.borderRadius['2xl']};
  margin-bottom: ${theme.spacing[8]};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  @media (max-width: ${theme.breakpoints.md}) {
    height: 400px;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[8]};
  margin-top: ${theme.spacing[12]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[8]};
`;

const BoardMember = styled.div`
  background: ${theme.colors.neutral.white};
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  h4 {
    color: ${theme.colors.primary.navy};
    margin-bottom: ${theme.spacing[1]};
    font-size: ${theme.fontSizes.lg};
  }

  p {
    color: ${theme.colors.neutral.gray};
    font-size: ${theme.fontSizes.sm};
    margin: 0;
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.neutral.white};
  padding: ${theme.spacing[10]} ${theme.spacing[8]};
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  text-align: center;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${theme.borderRadius['2xl']};
    background: linear-gradient(135deg, ${theme.colors.primary.gold}20, ${theme.colors.primary.lightGold}20);
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover {
    transform: scale(1.05) translateY(-12px) rotate(1deg);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);

    &::after {
      opacity: 1;
    }
  }
`;

const StatNumber = styled.div`
  font-size: 5rem;
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary.navy};
  margin-bottom: ${theme.spacing[4]};
  line-height: 1;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 4rem;
  }
`;

const StatLabel = styled.div`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary.navy};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: ${theme.spacing[4]};
  position: relative;
  z-index: 1;
`;

const StatDescription = styled.p`
  color: ${theme.colors.neutral.gray};
  line-height: ${theme.lineHeights.relaxed};
  margin: 0;
  font-size: ${theme.fontSizes.base};
  position: relative;
  z-index: 1;
`;

const HistoryHero = styled.div`
  background: ${theme.colors.primary.navy};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing[16]} ${theme.spacing[12]};
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: ${theme.shadows.xl};
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[12]} ${theme.spacing[8]};
  }
`;

const HistoryTitle = styled.h3`
  font-size: ${theme.fontSizes['4xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary.lightGold};
  margin: 0 0 ${theme.spacing[6]};
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes['3xl']};
  }
`;

const HistoryText = styled.div`
  position: relative;
  z-index: 1;

  p {
    color: ${theme.colors.secondary.ivory};
    line-height: ${theme.lineHeights.relaxed};
    margin-bottom: ${theme.spacing[4]};
    font-size: ${theme.fontSizes.xl};
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: ${theme.colors.primary.lightGold};
      font-weight: ${theme.fontWeights.semibold};
    }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[8]};
  margin-top: ${theme.spacing[16]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[6]};
    margin-top: ${theme.spacing[12]};
  }
`;

interface BoardMemberData {
  id: string;
  name: string;
  role: string;
}

export const AboutPage = () => {
  const [boardMembers, setBoardMembers] = useState<BoardMemberData[]>([]);
  const [totalMembers, setTotalMembers] = useState(180); // Default fallback

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, membersRes] = await Promise.all([
          client.get('/settings'),
          client.get('/board-members'),
        ]);

        if (membersRes.data) {
          setBoardMembers(membersRes.data);
        }

        if (settingsRes.data) {
          const { count_harmonie, count_precilia, count_trommelkorps, count_popcilia } = settingsRes.data;
          const total = 
            (parseInt(count_harmonie) || 0) + 
            (parseInt(count_precilia) || 0) + 
            (parseInt(count_trommelkorps) || 0) + 
            (parseInt(count_popcilia) || 0);
          
          const roundedTotal = Math.floor(total / 10) * 10;
          setTotalMembers(roundedTotal);
        }
      } catch (error) {
        console.error('Failed to fetch data', error);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <PageHeader>
        <Container>
          <FadeInWhenVisible>
            <PageTitle>Koninklijke Poperingse Harmonie Sint-Cecilia</PageTitle>
            <PageSubtitle>
              Opgericht in 1831 en nog steeds springlevend
            </PageSubtitle>
          </FadeInWhenVisible>
        </Container>
      </PageHeader>

      <Section>
        <Container>
          <FadeInWhenVisible>
            <h2 style={{
              textAlign: 'center',
              marginBottom: theme.spacing[16],
              color: theme.colors.primary.navy,
              fontSize: theme.fontSizes['5xl'],
              fontWeight: theme.fontWeights.bold,
              letterSpacing: '-0.02em'
            }}>
              Onze Geschiedenis
            </h2>
          </FadeInWhenVisible>

          <FadeInWhenVisible>
            <HistoryHero>
              <HistoryTitle>Bijna 200 Jaar Muzikale Traditie</HistoryTitle>
              <HistoryText>
                <p>
                  <strong>De Koninklijke Poperingse Harmonie Sint-Cecilia werd opgericht in 1831</strong> en draagt
                  sindsdien de muzikale traditie van Poperinge hoog in het vaandel.
                </p>
                <p>
                  Met bijna twee eeuwen geschiedenis zijn we één van de oudste harmonieën in de regio.
                  Door de jaren heen heeft onze harmonie zich ontwikkeld tot een bloeiende muzikale
                  organisatie die bekend staat om haar kwaliteit en gemeenschapsbetrokkenheid.
                </p>
              </HistoryText>

              <StatsContainer>
                <StatCard>
                  <StatNumber>
                    <AnimatedCounter end={1831} duration={3000} countDown={true} />
                  </StatNumber>
                  <StatLabel>Opgericht</StatLabel>
                  <StatDescription>
                    Bijna twee eeuwen muzikale traditie in Poperinge
                  </StatDescription>
                </StatCard>

                <StatCard>
                  <StatNumber>
                    <AnimatedCounter key={totalMembers} end={totalMembers} duration={2000} suffix="+" />
                  </StatNumber>
                  <StatLabel>Leden</StatLabel>
                  <StatDescription>
                    Verdeeld over vier ensembles, van beginners tot gevorderden
                  </StatDescription>
                </StatCard>

                <StatCard>
                  <StatNumber>
                    <AnimatedCounter end={2023} duration={2500} countDown={true} />
                  </StatNumber>
                  <StatLabel>VLAMO Ereafdeling</StatLabel>
                  <StatDescription>
                    Erkend voor muzikale excellentie en toewijding
                  </StatDescription>
                </StatCard>
              </StatsContainer>
            </HistoryHero>
          </FadeInWhenVisible>
        </Container>
      </Section>

      <Section background={theme.colors.secondary.ivory}>
        <Container>
          <ContentSection>
            <FadeInWhenVisible direction="left">
              <DirectorImage src={bjornPhoto} alt="Björn De Kock - Dirigent KPH" />
            </FadeInWhenVisible>

            <FadeInWhenVisible direction="right">
              <TextContent>
                <h2>Onze Dirigent</h2>
                <h3>Björn De Kock</h3>
                <p>
                  Sinds begin 2008 staat Björn De Kock aan het roer van KPH Sint-Cecilia. Hij nam
                  de dirigeerstok over van José Moerenhout, die na 19 jaar de fakkel doorgaf aan nieuw, gepassioneerd talent.
                </p>
                <p>
                  Björn werd geboren op 6 maart 1977 in Ieper en studeerde piano en slagwerk aan de
                  Stedelijke Academie voor Muziek van Ieper. Hij behaalde zijn Master in de Muziek
                  aan het Koninklijk Conservatorium van Antwerpen, met specialisaties in
                  slagwerkinstrumenten en HAFABRA-directie.
                </p>
                <p>
                  Al op zestienjarige leeftijd begon Björn met het dirigeren van ensembles. Zijn
                  ervaring omvat het leiden van jeugdharmonieën, drumfanfares en slagwerkgroepen
                  doorheen Vlaanderen. Daarnaast speelde hij drums bij rockband Panopticum en later
                  bij "The Pritty Boyz".
                </p>
                <p>
                  Momenteel geeft Björn ook les slagwerk aan de Stedelijke Academie voor Muziek
                  van Ieper, waar hij zijn passie en kennis deelt met de volgende generatie muzikanten.
                </p>
              </TextContent>
            </FadeInWhenVisible>
          </ContentSection>
        </Container>
      </Section>

      <Section>
        <Container>
          <FadeInWhenVisible>
            <h2 style={{ textAlign: 'center', marginBottom: theme.spacing[4] }}>
              Ons Bestuur
            </h2>
            <p style={{ textAlign: 'center', color: theme.colors.neutral.gray, maxWidth: '750px', margin: '0 auto', marginBottom: theme.spacing[8] }}>
              Het bestuur begeleidt samen met de dirigent de energie van alle leden in de juiste richting
            </p>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.1}>
            <BoardImage src={boardPhoto} alt="Het Bestuur van KPH Sint-Cecilia" />
          </FadeInWhenVisible>

          <BoardGrid>
            {boardMembers.map((member, index) => (
              <FadeInWhenVisible key={member.id} delay={0.1 + index * 0.05}>
                <BoardMember>
                  <h4>{member.name}</h4>
                  <p>{member.role}</p>
                </BoardMember>
              </FadeInWhenVisible>
            ))}
            {boardMembers.length === 0 && (
              <p style={{ textAlign: 'center', gridColumn: '1/-1', color: theme.colors.neutral.gray }}>
                Laden bestuur...
              </p>
            )}
          </BoardGrid>
        </Container>
      </Section>

      <Section background={theme.colors.secondary.ivory}>
        <Container>
          <FadeInWhenVisible>
            <h2 style={{ textAlign: 'center', marginBottom: theme.spacing[12] }}>
              Onze Waarden
            </h2>
          </FadeInWhenVisible>

          <ValuesGrid>
            <FadeInWhenVisible delay={0.1}>
              <IconBox>
                <IconCircle>
                  <History size={28} />
                </IconCircle>
                <IconContent>
                  <h3>Traditie & Innovatie</h3>
                  <p>
                    Sinds 1831 koesteren we onze rijke geschiedenis terwijl we open staan voor
                    nieuwe muzikale invloeden en moderne technieken.
                  </p>
                </IconContent>
              </IconBox>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.2}>
              <IconBox>
                <IconCircle>
                  <Target size={28} />
                </IconCircle>
                <IconContent>
                  <h3>Excellentie</h3>
                  <p>
                    We streven naar de hoogste muzikale kwaliteit door regelmatige repetities,
                    professionele begeleiding en continue ontwikkeling.
                  </p>
                </IconContent>
              </IconBox>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.3}>
              <IconBox>
                <IconCircle>
                  <Heart size={28} />
                </IconCircle>
                <IconContent>
                  <h3>Passie</h3>
                  <p>
                    Muziek zit in ons hart. Onze toewijding en ons enthousiasme zijn voelbaar in elk
                    optreden en elke noot die we spelen.
                  </p>
                </IconContent>
              </IconBox>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.4}>
              <IconBox>
                <IconCircle>
                  <Users2 size={28} />
                </IconCircle>
                <IconContent>
                  <h3>Gemeenschap</h3>
                  <p>
                    Met meer dan 180 leden vormen we een hechte muzikale familie, van jong tot oud,
                    en zijn we nauw verbonden met onze lokale gemeenschap.
                  </p>
                </IconContent>
              </IconBox>
            </FadeInWhenVisible>
          </ValuesGrid>
        </Container>
      </Section>

      <Section>
        <Container>
          <FadeInWhenVisible>
            <TextContent style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
              <h2>Word Lid</h2>
              <p>
                Ben je geïnteresseerd om lid te worden van onze muzikale familie? We verwelkomen
                muzikanten van alle leeftijden en niveaus. Of je nu een ervaren muzikant bent of
                net begint met één van onze beginnersgroepen, bij KPH Sint-Cecilia vind je een
                plaats waar je je talent kunt ontwikkelen.
              </p>
              <p>
                We repeteren elke vrijdagavond de 2de verdiepping in de Kring, huis van verenigingen in Poperinge.
                Neem contact met ons op via <a href="mailto:info@kph.be" style={{ color: theme.colors.primary.royalBlue }}>info@kph.be</a> voor
                meer informatie over lidmaatschap en wat we van onze leden verwachten.
              </p>
              <p>
                We kijken ernaar uit om van je te horen!
              </p>
            </TextContent>
          </FadeInWhenVisible>
        </Container>
      </Section>
    </>
  );
};
